import { useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import FaceIdentityBrandLogo from '../../face-identity/components/FaceIdentityBrandLogo';
import FaceIdentityFlowNav from '../../face-identity/components/FaceIdentityFlowNav';
import RegisterServicePicker from '../../face-identity/components/RegisterServicePicker';
import { useFaceIdentityOrgLogo } from '../../face-identity/hooks/useFaceIdentityOrgLogo';
import { useFaceIdentityScrollShell } from '../../face-identity/hooks/useFaceIdentityScrollShell';
import { useRegisterServicesCatalog } from '../../face-identity/hooks/useRegisterServices';
import { buildKioskNavState } from '../../face-identity/utils/kioskNavState';
import { SALON_SERVICE_LOBBY_PATH } from '../config/salonApp';
import { resolveSalonHomePath } from '../utils/salonHomeNav';
import { serviceSessionHeroImage } from '../utils/serviceSessionVisuals';
import ServiceSessionHubChooseCard from '../components/ServiceSessionHubChooseCard';
import '../../face-identity/styles/FaceIdentityLuxTheme.css';
import '../../face-identity/styles/FaceIdentityScrollShell.css';
import './ServiceSessionPage.css';

const GENDERS = [
  { id: 'female', label: 'Women' },
  { id: 'male', label: 'Men' },
];

export default function ServiceCatalogPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { orgLogoUrl, showLogo, onLogoError } = useFaceIdentityOrgLogo();
  useFaceIdentityScrollShell();
  const homePath = resolveSalonHomePath();
  const navState = buildKioskNavState(location.state);
  const [gender, setGender] = useState('female');
  const [categoryRailHost, setCategoryRailHost] = useState(null);
  const { services, loading, error } = useRegisterServicesCatalog(gender, { requireGender: true });
  const heroImageUrl = useMemo(() => serviceSessionHeroImage(gender), [gender]);

  return (
    <div className="fi-lux-theme fi-scroll-page page-bg salon-service-session salon-service-session--flow salon-service-session--start salon-catalog-menu">
      <div className="salon-service-session__content salon-service-start">
        <header className="salon-service-session__brand">
          <FaceIdentityBrandLogo
            showLogo={showLogo}
            orgLogoUrl={orgLogoUrl}
            onLogoError={onLogoError}
          />
          <FaceIdentityFlowNav
            homePath={homePath}
            onBack={() => navigate(SALON_SERVICE_LOBBY_PATH, { state: navState })}
          />
        </header>

        <div className="salon-guest-pick__sheet salon-service-start__sheet">
          <div className="salon-service-start__workspace salon-service-start__workspace--aligned">
            <div className="salon-service-start__hero-row">
              <div className="salon-service-start__hub-slot">
                <ServiceSessionHubChooseCard />
              </div>
              <section className="salon-service-start__hero salon-service-start__hero--banner">
                <div className="salon-service-start__hero-copy">
                  <p className="salon-guest-pick__eyebrow">Service menu</p>
                  <h1 className="salon-service-session__title salon-guest-pick__name salon-service-start__hero-greeting">
                    Our services
                  </h1>
                  <p className="salon-service-session__lede salon-guest-pick__meta salon-service-start__hero-lede">
                    {gender === 'male' ? "Men's menu" : "Women's menu"}
                  </p>
                </div>
                <div className="salon-service-start__hero-art-wrap">
                  <img
                    className={
                      gender === 'male'
                        ? 'salon-service-start__hero-art salon-service-start__hero-art--male'
                        : 'salon-service-start__hero-art salon-service-start__hero-art--female'
                    }
                    src={heroImageUrl}
                    alt=""
                    aria-hidden="true"
                    decoding="async"
                  />
                </div>
              </section>
            </div>

            <div className="salon-service-start__left-stack">
              <aside
                className="salon-service-start__category-rail-host"
                ref={setCategoryRailHost}
                aria-label="Service category menu"
              />
            </div>

            <section className="salon-service-session__panel salon-guest-pick__panel salon-service-start__services">
              <div className="salon-catalog-menu__gender" role="group" aria-label="Menu">
                {GENDERS.map((item) => (
                  <button
                    key={item.id}
                    type="button"
                    className={gender === item.id ? 'is-active' : ''}
                    aria-pressed={gender === item.id}
                    onClick={() => setGender(item.id)}
                  >
                    {item.label}
                  </button>
                ))}
              </div>
              {error ? (
                <p className="salon-service-session__error">{error}</p>
              ) : (
                <RegisterServicePicker
                  services={services}
                  selected={[]}
                  browseOnly
                  categoryTabs
                  categoryTabsLayout="sidebar"
                  categoryRailHost={categoryRailHost}
                  cardLayout="stack"
                  variant="cards"
                  visualBundle="service-session"
                  visualGender={gender}
                  showSearch
                  hint=""
                  title={loading ? 'Loading…' : 'Services'}
                />
              )}
            </section>
          </div>
        </div>
      </div>
    </div>
  );
}
